import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { matchRoutes, useLocation } from "react-router-dom";
import { getPurchaseOrders } from "../api/ProcurementClient";
import PurchaseOrder from "../models/PurchaseOrder";
import { POStatus } from "../models/POStatus";
import {
  ALL_ITEMS_URL,
  COMPLETED_POS_URL,
  ORDERS_URL,
  PRICE_QUOTES_URL,
  QUEUE_URL,
  VENDOR_ORDERS_URL,
  ZENKIT_URL,
} from "../MainAppPanel";

const WORK_QUEUE_PO_POLL_MS = 60_000;

function purchaseOrderListFingerprint(pos: PurchaseOrder[]): string {
  return pos
    .map(
      (po) =>
        `${po.id}:${po.status}:${po.quoteId ?? ""}:${po.emailId ?? ""}:${po.rfqId ?? ""}`
    )
    .join("|");
}

type WorkQueuePurchaseOrdersContextValue = {
  purchaseOrders: PurchaseOrder[];
  inboundDraftPurchaseOrders: PurchaseOrder[];
  refreshPurchaseOrders: () => Promise<void>;
};

const WorkQueuePurchaseOrdersContext =
  createContext<WorkQueuePurchaseOrdersContextValue>({
    purchaseOrders: [],
    inboundDraftPurchaseOrders: [],
    refreshPurchaseOrders: async () => {},
  });

function isWorkQueueListPath(pathname: string): boolean {
  return Boolean(
    matchRoutes(
      [
        { path: QUEUE_URL },
        { path: ORDERS_URL },
        { path: VENDOR_ORDERS_URL },
        { path: COMPLETED_POS_URL },
        { path: ALL_ITEMS_URL },
        { path: PRICE_QUOTES_URL },
        { path: ZENKIT_URL },
        { path: `${ZENKIT_URL}/v/:viewId` },
      ],
      pathname
    )
  );
}

/**
 * Shared poll of GET /purchase-order?workQueue=true for work-queue tabs + list pages.
 * Skips fetch/poll on unrelated routes (e.g. open RFQ detail, admin).
 */
export const WorkQueuePurchaseOrdersProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const pollList = isWorkQueueListPath(location.pathname);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const cancelledRef = useRef(false);

  const refreshPurchaseOrders = useCallback(async () => {
    try {
      const list = await getPurchaseOrders({ workQueue: true });
      if (cancelledRef.current) return;
      setPurchaseOrders((prev) =>
        purchaseOrderListFingerprint(prev) ===
        purchaseOrderListFingerprint(list)
          ? prev
          : list
      );
    } catch {
      // Keep last good list — clearing to [] flashes RFQs between Work Queue
      // and Vendor Orders on every failed poll.
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    if (!pollList) {
      return () => {
        cancelledRef.current = true;
      };
    }

    void refreshPurchaseOrders();
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void refreshPurchaseOrders();
    }, WORK_QUEUE_PO_POLL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshPurchaseOrders();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelledRef.current = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [pollList, refreshPurchaseOrders]);

  const inboundDraftPurchaseOrders = useMemo(
    () =>
      purchaseOrders.filter(
        (po) => po.status === POStatus.DRAFT && po.emailId != null
      ),
    [purchaseOrders]
  );

  const value = useMemo(
    () => ({
      purchaseOrders,
      inboundDraftPurchaseOrders,
      refreshPurchaseOrders,
    }),
    [purchaseOrders, inboundDraftPurchaseOrders, refreshPurchaseOrders]
  );

  return (
    <WorkQueuePurchaseOrdersContext.Provider value={value}>
      {children}
    </WorkQueuePurchaseOrdersContext.Provider>
  );
};

export function useWorkQueuePurchaseOrders(): WorkQueuePurchaseOrdersContextValue {
  return useContext(WorkQueuePurchaseOrdersContext);
}
